import 'aiprise-web-sdk';

import { SxProps, Theme, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import {
  AiPriseBusinessProfileEvent,
  AiPriseDataSavedEvent,
  AiPriseErrorEvent,
  AiPriseSessionEvent,
} from 'aiprise-web-sdk';
import React from 'react';

import { configuration } from '@/config';
import {
  ENVIRONMENT,
  IS_PREVIEW_PRODUCTION_ENVIRONMENT,
  IS_PRODUCTION_ENVIRONMENT,
} from '@/constants';
import { ColorSchemeValueContext } from '@/context/colorSchemeContext';
import { LocaleContext } from '@/hooks/useTranslator';
import { BeneficiaryType } from '@/types/customer';
import { Locale } from '@/types/locale';

interface CommonProps {
  readonly onError?: (event: AiPriseErrorEvent) => void;
  readonly onSuccessful?: (event: AiPriseSessionEvent) => void;
  readonly onContinue?: (event: AiPriseSessionEvent) => void;
  readonly sx?: SxProps<Theme>;
}

interface NewSessionProps {
  readonly sessionId?: never;
  readonly onStarted?: (event: AiPriseSessionEvent) => void;
}

interface ExistingSessionProps {
  readonly sessionId: string;
  readonly onResumed?: (event: AiPriseSessionEvent) => void;
  readonly onDataSaved?: (event: AiPriseDataSavedEvent) => void;
  readonly onDataRestored?: (event: AiPriseSessionEvent) => void;
}

interface BusinessNewSessionProps extends CommonProps, NewSessionProps {
  readonly customerType: BeneficiaryType.business;
  readonly onBusinessProfile?: (event: AiPriseBusinessProfileEvent) => void;
}

interface BusinessExistingSessionProps extends CommonProps, ExistingSessionProps {
  readonly customerType: BeneficiaryType.business;
  readonly onBusinessProfile?: (event: AiPriseBusinessProfileEvent) => void;
}

interface IndividualNewSessionProps extends CommonProps, NewSessionProps {
  readonly customerType: BeneficiaryType.individual;
}

interface IndividualExistingSessionProps extends CommonProps, ExistingSessionProps {
  readonly customerType: BeneficiaryType.individual;
}

type Props =
  | BusinessNewSessionProps
  | BusinessExistingSessionProps
  | IndividualNewSessionProps
  | IndividualExistingSessionProps;

const callbackUrlByEnvironment: Record<NonNullable<typeof ENVIRONMENT>, string> = {
  local: configuration.aipriseTemplateCallbackUrlLocal,
  'preview-develop': configuration.aipriseTemplateCallbackUrlDevelop,
  develop: configuration.aipriseTemplateCallbackUrlDevelop,
  'preview-production': configuration.aipriseTemplateCallbackUrlProduction,
  production: configuration.aipriseTemplateCallbackUrlProduction,
};

const callbackUrl = ENVIRONMENT ? callbackUrlByEnvironment[ENVIRONMENT] : undefined;

const buildThemeOptions = (
  colorScheme: 'light' | 'dark',
  theme: Theme,
): Record<string, string> => ({
  // Theme mode
  background: colorScheme,

  // Brand colors
  color_brand: theme.palette.primary.main,
  color_brand_overlay: theme.palette.primary.contrastText,
  color_page: theme.palette.background.paper,

  // Typography
  font_name: 'Lexend',
  font_weights: '400,500',

  // Button styling
  button_border_radius: '28px',
  button_padding: '16px 24px',
  button_font_size: '16px',
  button_font_weight: '400',

  // Input field styling
  input_border_radius: '24px',
  input_padding: '23px 32px',
  input_font_size: '16px',
  input_font_weight: '400',
  input_label_font_size: '16px',
  input_label_font_weight: '500',

  // Layout styling
  layout_border_radius: '16px',
  modal_border_radius: '16px',
  image_border_radius: '12px',
});

export const AiPriseFrame: React.FC<Props> = (props: Props): React.ReactElement => {
  const { customerType, sessionId, onError, onSuccessful, onContinue, sx: sxProps } = props;
  const locale = React.useContext<Locale>(LocaleContext);
  const colorScheme = React.useContext(ColorSchemeValueContext);
  const theme = useTheme();
  const aipriseRef = React.useRef<HTMLElement>(null);
  const sx = React.useMemo(
    (): SxProps<Theme> => (sxProps ? { ...styles.container, ...sxProps } : styles.container),
    [sxProps],
  );

  const templateId =
    customerType === BeneficiaryType.business
      ? configuration.aipriseTemplateIdBusiness
      : customerType === BeneficiaryType.individual
        ? configuration.aipriseTemplateIdIndividual
        : '';

  const uiOptions = React.useMemo((): string => {
    return JSON.stringify({
      common: {
        default_language: locale,
      },
    });
  }, [locale]);

  const themeOptions = React.useMemo((): string => {
    const options = buildThemeOptions(colorScheme, theme);

    return JSON.stringify(options);
  }, [colorScheme, theme]);

  // Extract session-specific handlers
  const onStarted = sessionId === undefined ? props.onStarted : undefined;
  const onResumed = sessionId !== undefined ? props.onResumed : undefined;
  const onDataSaved = sessionId !== undefined ? props.onDataSaved : undefined;
  const onDataRestored = sessionId !== undefined ? props.onDataRestored : undefined;

  // Extract business-specific handler
  const onBusinessProfile =
    props.customerType === BeneficiaryType.business ? props.onBusinessProfile : undefined;
  const hasOnBusinessProfile = onBusinessProfile && customerType === BeneficiaryType.business;

  React.useEffect((): (() => void) | void => {
    if (!aipriseRef.current) {
      return;
    }

    const element = aipriseRef.current;

    if (onStarted) {
      element.addEventListener('aiprise:started', onStarted);
    }
    if (onError) {
      element.addEventListener('aiprise:error', onError);
    }
    if (onSuccessful) {
      element.addEventListener('aiprise:successful', onSuccessful);
    }
    if (onContinue) {
      element.addEventListener('aiprise:continue', onContinue);
    }
    if (onResumed) {
      element.addEventListener('aiprise:resumed', onResumed);
    }
    if (onDataSaved) {
      element.addEventListener('aiprise:data-saved', onDataSaved);
    }
    if (onDataRestored) {
      element.addEventListener('aiprise:data-restored', onDataRestored);
    }
    if (hasOnBusinessProfile) {
      element.addEventListener('aiprise:business-profile', onBusinessProfile);
    }

    return (): void => {
      if (onStarted) {
        element.removeEventListener('aiprise:started', onStarted);
      }
      if (onError) {
        element.removeEventListener('aiprise:error', onError);
      }
      if (onSuccessful) {
        element.removeEventListener('aiprise:successful', onSuccessful);
      }
      if (onContinue) {
        element.removeEventListener('aiprise:continue', onContinue);
      }
      if (onResumed) {
        element.removeEventListener('aiprise:resumed', onResumed);
      }
      if (onDataSaved) {
        element.removeEventListener('aiprise:data-saved', onDataSaved);
      }
      if (onDataRestored) {
        element.removeEventListener('aiprise:data-restored', onDataRestored);
      }
      if (hasOnBusinessProfile) {
        element.removeEventListener('aiprise:business-profile', onBusinessProfile);
      }
    };
  }, [
    onStarted,
    onError,
    onSuccessful,
    onContinue,
    onResumed,
    onDataSaved,
    onDataRestored,
    onBusinessProfile,
    customerType,
    hasOnBusinessProfile,
  ]);

  return (
    <Box sx={sx}>
      <aiprise-frame
        ref={aipriseRef}
        template-id={templateId}
        session-id={sessionId}
        mode={
          IS_PRODUCTION_ENVIRONMENT || IS_PREVIEW_PRODUCTION_ENVIRONMENT ? 'PRODUCTION' : 'SANDBOX'
        }
        ui-options={uiOptions}
        callback-url={callbackUrl}
        events-callback-url={callbackUrl}
        theme={themeOptions}
      />
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    height: 650,
  },
};
